// workload.tsx (Full updated component — virtualization both sides + scroll-sync + load-more on right)
import React, {
  useRef,
  useEffect,
  useState,
  SyntheticEvent,
  useMemo,
  useCallback,
} from "react";

import styles from "./workload.module.css";
import dateFormater from "@/helperFuntions/dateFormater";
import Select from "react-select";
import UserAvatar from "@/components/ui/userAvatar";
import { UserType } from "@/api/query/useCurrentUserQuery";
import { useTranslation } from "react-i18next";
import { AssignedUsers } from "@/api/query/useProjectQuery";
import { ViewMode } from "../../types/public-types";
import { ChevronRight, ChevronDown } from "lucide-react";
import { countLeaveDaysInRange } from "@/helperFuntions/checkUserLeave";
import Loader from "@/components/common/Loader";
import { downloadWorkloadXlsx } from "./downloadWorkloadXlsx";
import { Button } from "@/components/ui/button";

// --------------------- CONFIG ---------------------
const INITIAL_VISIBLE_ROWS = 12; // initial rows shown
const LOAD_MORE_COUNT = 10; // rows to add when scrolled to bottom
const BOTTOM_THRESHOLD = 120; // px from bottom to trigger load-more

// --------------------- TYPES ---------------------
type TooltipState = {
  visible: boolean;
  x: number;
  y: number;
  content: string;
};

type Resource = {
  id: string;
  name: string;
  type: "user" | "project" | "task";
  user?: UserType;
  children?: Resource[];
  totalHours?: number;
  // optional userAvailability kept as-is from your model
  userAvailability?: any[];
};

type DailyWorkload = Map<string, Map<string, number>>;

interface AssignedUser {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface Assignment {
  user?: AssignedUser;
  workingHours?: number;
}

interface BarTaskTyped {
  start: Date;
  end: Date;
  assign?: Assignment[];
  duration: number;
  type: string;
}

type TaskWithSubtasks = BarTaskTyped & {
  id: string;
  name: string;
  subtask?: TaskWithSubtasks[];
};

type FlatRow = {
  node: Resource;
  level: number;
  id: string;
};

// --------------------- styles for react-select (kept) ---------------------
const reactSelectStyle = {
  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...provided,
    border: "1px solid #E7E7E7",
    paddingTop: "0.0rem",
    paddingBottom: "0.0rem",
    outline: state.isFocused ? "2px solid #535EF9" : "0px solid #E7E7E7",
    boxShadow: state.isFocused ? "0px 0px 0px #535EF9" : "none",
    "&:hover": {
      outline: state.isFocused ? "1px solid #535EF9" : "1px solid #E7E7E7",
      boxShadow: "0px 0px 0px #535EF9",
    },
  }),
  option: (provided: Record<string, unknown>, state: { isSelected: boolean }) => ({
    ...provided,
    color: state.isSelected ? "white" : "black",
    background: state.isSelected ? "#535EF9" : "white",
    "&:hover": {
      color: "white",
      background: "#535EF999",
    },
  }),
};

// --------------------- helper functions (copied / retained) ---------------------
const createIsWorkingDayChecker = (nonWorkingDays: number[], holidays: Date[]) => {
  const holidaySet = new Set(holidays.map((d) => dateFormater(d)));
  const nonWorkingDaySet = new Set(nonWorkingDays);
  const cache = new Map<string, boolean>();

  return (date: Date): boolean => {
    const key = dateFormater(date);
    if (cache.has(key)) return cache.get(key)!;

    const dayOfWeek = date.getDay();
    const isWorking = !nonWorkingDaySet.has(dayOfWeek) && !holidaySet.has(key);
    cache.set(key, isWorking);
    return isWorking;
  };
};
const toLocalDate = (d: Date | string) => {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};

// Parse your dd/mm/yyyy formatted dateKey into a local Date
const parseDateKey = (dateKey: string) => {
  if (!dateKey) return null;
  const parts = dateKey.split("/").map(Number);
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  return new Date(yyyy, mm - 1, dd);
};
``
const createViewKeyFinder = (dates: Date[], viewMode: ViewMode) => {
  const cache = new Map<string, string>();
  // ensure the dates array contains period START dates in local midnight form
  const sortedDates = [...dates].map(toLocalDate).sort((a, b) => a.getTime() - b.getTime());

  return (inputDate: Date): string => {
    const date = toLocalDate(inputDate);
    const dateKey = dateFormater(date);

    if (cache.has(dateKey)) return cache.get(dateKey)!;

    if (viewMode === ViewMode.Day) {
      cache.set(dateKey, dateKey);
      return dateKey;
    }

    for (const periodStart of sortedDates) {
      const start = toLocalDate(periodStart);
      const end = toLocalDate(start);

      if (viewMode === ViewMode.Week) {
        end.setDate(start.getDate() + 6);
      } else if (viewMode === ViewMode.Month) {
        end.setMonth(start.getMonth() + 1, 0);
      } else if (viewMode === ViewMode.QuarterYear) {
        // start is already the start of the quarter in your dates[] — treat quarter as 3 months
        end.setMonth(start.getMonth() + 3, 0);
      } else if (viewMode === ViewMode.Year) {
        end.setFullYear(start.getFullYear() + 1, 0, 0);
      }

      if (date >= start && date <= end) {
        const resultKey = dateFormater(start); // canonical period start key
        cache.set(dateKey, resultKey);
        return resultKey;
      }
    }

    cache.set(dateKey, "");
    return "";
  };
};


// Monday-based week start


const createWorkableHoursCalculator = (
  viewMode: ViewMode,
  isWorkingDay: (date: Date) => boolean
) => {
  const cache = new Map<string, number>();
  const USER_HOURS_PER_DAY = 8;

  return (periodStartDate: Date): number => {
    const key = dateFormater(periodStartDate);
    if (cache.has(key)) return cache.get(key)!;
    
    if (viewMode === ViewMode.Day) return USER_HOURS_PER_DAY;

    const startDate = new Date(periodStartDate);
    const endDate = new Date(startDate);

    switch (viewMode) {
      case ViewMode.Week:
        endDate.setDate(startDate.getDate() + 6);
        break;
      case ViewMode.Month:
        endDate.setMonth(startDate.getMonth() + 1, 0);
        break;
      case ViewMode.QuarterYear:
        endDate.setMonth(startDate.getMonth() + 3, 0);
        break;
      case ViewMode.Year:
        endDate.setFullYear(startDate.getFullYear() + 1, 0, 0);
        break;
    }

    let workingDays = 0;
    const currentDay = new Date(startDate);
    while (currentDay <= endDate) {
      if (isWorkingDay(currentDay)) workingDays++;
      currentDay.setDate(currentDay.getDate() + 1);
    }

    const totalHours = workingDays * USER_HOURS_PER_DAY;
    cache.set(key, totalHours);
    return totalHours;
  };
};

const getWorkloadColor = (hours: number, inPercentage: boolean) => {
  if (inPercentage) {
    return hours > 100 ? "rgba(255, 99, 132, 0.4)" : hours > 0 ? "rgba(83, 94, 249, 0.2)" : "transparent";
  }
  return hours > 8 ? "rgba(255, 99, 132, 0.4)" : hours > 0 ? "rgba(83, 94, 249, 0.2)" : "transparent";
};

// --------------------- workload calculation functions (kept as-is) ---------------------
// (I copied your implementations unchanged — calculateProjectWorkloadData,
// extractAllDates, calculateWorkloadData — omitted here to keep message shorter.
// In your file keep the original ones you already had above.)
// For this paste, include your existing implementations for those helper functions.
// -------------------------------------------------------------------------------
// NOTE: In the file below I reference calculateProjectWorkloadData and calculateWorkloadData
// — keep your original implementations as they were in your earlier file.
// -------------------------------------------------------------------------------

// --------------------- flatten helper ---------------------
const flattenResources = (resources: Resource[], expanded: string[]): FlatRow[] => {
  const result: FlatRow[] = [];
  const walk = (node: Resource, level = 0) => {
    result.push({ node, level, id: node.id });
    const isExpanded = expanded.includes(node.id); // in your earlier code `expanded` stored ids to hide children
    if (node.children && node.children.length && isExpanded) {
      for (const child of node.children) {
        walk(child, level + 1);
      }
    }
  };

  for (const r of resources) walk(r, 0);
  return result;
};

// --------------------- WorkloadGrid (virtualized, right) ---------------------
const WorkloadGrid: React.FC<{
  flatRows: FlatRow[]; // full flattened rows array (but we'll pass only visible slice or full if desired)
  dates: Date[];
  workloadData: DailyWorkload;
  rowHeight: number;
  columnWidth: number;
  nonWorkingDays: number[];
  holidays: Date[];
  inPercentage: boolean;
  setTooltip: React.Dispatch<React.SetStateAction<TooltipState>>;
  viewMode: ViewMode;
}> = React.memo(({
  flatRows,
  dates,
  workloadData,
  rowHeight,
  columnWidth,
  nonWorkingDays,
  holidays,
  inPercentage,
  setTooltip,
  viewMode,
}) => {
  const { t } = useTranslation();

  const isWorkingDay = useMemo(
    () => createIsWorkingDayChecker(nonWorkingDays, holidays),
    [nonWorkingDays, holidays]
  );

  // Render the passed rows (they should be visible slice already).
  return (
    <g className="workload-grid-body">
      {flatRows.map((fr, vizIndex) => {
        // fr.originalIndex will come from the id+index in the caller slice; we need the original index:
        // Here we assume caller passed rows such that each fr contains original index as part of id and position.
        // But simpler: caller sends fr with the property 'originalIndex' if necessary. For our usage,
        // caller will call flatRows.slice(0, visibleCount) — so vizIndex == originalIndex.
        const originalIndex = fr as FlatRow & { originalIndex?: number };
        const idx = (originalIndex as any).originalIndex ?? vizIndex;
        const resource = fr.node;
        const y = idx * rowHeight;
        
        const userWorkload = workloadData.get(resource.id);
        const userAvailability = resource.user?.userAvailability ?? resource.userAvailability;

        return (
          <g key={`${resource.id}-${idx}`} data-component="workload">
            <line x1={0} y1={y + rowHeight} x2={dates.length * columnWidth} y2={y + rowHeight} className={styles.gridRowLine} />
            <line x1={0} y1={y} x2={dates.length * columnWidth} y2={y} className={styles.gridRowLine} />
            
            {dates.map((date, j) => {
              const dateKey = dateFormater(date);
              const x = j * columnWidth;
              const hours = userWorkload?.get(dateKey) ?? 0;
              const isWorkday = isWorkingDay(date);
              let maxHours = 8;
              let fillColor: string;
              if (viewMode === ViewMode.Day) {
                maxHours = isWorkday ? 8 : 0;
                fillColor = maxHours === 0
                  ? "rgba(80, 80, 80, 0.05)"
                  : hours > maxHours && !inPercentage
                  ? "rgba(255, 99, 132, 0.4)"
                  : getWorkloadColor(hours, inPercentage);
              } else {
                const start = new Date(date);
                const end = new Date(date);

                if (viewMode === ViewMode.Week) {
                  end.setDate(start.getDate() + 6);
                } else if (viewMode === ViewMode.Month) {
                  end.setMonth(start.getMonth() + 1, 0);
                } else if (viewMode === ViewMode.QuarterYear) {
                  const q = Math.floor(start.getMonth() / 3);
                  end.setMonth(q * 3 + 3, 0);
                } else if (viewMode === ViewMode.Year) {
                  end.setFullYear(start.getFullYear() + 1, 0, 0);
                }

                let workingDays = 0;
                const current = new Date(start);
                while (current <= end) {
                  if (isWorkingDay(current)) workingDays++;
                  current.setDate(current.getDate() + 1);
                }
                maxHours = workingDays * 8;

                fillColor = inPercentage
                  ? getWorkloadColor(hours, inPercentage)
                  : hours > 0
                  ? hours > maxHours ? "rgba(255, 99, 132, 0.4)" : "rgba(83, 94, 249, 0.2)"
                  : "white";
              }

              const isOverloaded = fillColor === "rgba(255, 99, 132, 0.4)";
              const normalizeDate = (d: Date | string) => {
                const dateObj = new Date(d);
                return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
              };
              
              const isAbsent = userAvailability?.some((r) => {
                const start = r.startDate ? normalizeDate(r.startDate) : null;
                const end = r.endDate ? normalizeDate(r.endDate) : null;
                const current = normalizeDate(date);
              
                if (start && end) return current >= start && current <= end;
                if (start && !end) return current.getTime() === start.getTime();
              
                return false;
              });
              
              return (
                <g
                  key={`${resource.id}-${j}`}
                  onMouseEnter={(e) => {
                    const ev = e as unknown as MouseEvent & { clientX: number; clientY: number };
                    if (!isAbsent && isOverloaded) {
                      setTooltip({
                        visible: true,
                        x: ev.clientX,
                        y: ev.clientY,
                        content: t("tooltip.overload", {
                          date: dateFormater(date),
                          interpolation: { escapeValue: false },
                        }),
                      });
                    } else if (isAbsent) {
                      setTooltip({
                        visible: true,
                        x: ev.clientX,
                        y: ev.clientY,
                        content: t("this user is absent this day"),
                      });
                    }
                  }}
                  onMouseLeave={() => setTooltip((prev) => ({ ...prev, visible: false }))}
                  cursor={isOverloaded || isAbsent ? "pointer" : "default"}
                >
                  <rect
                    x={x}
                    y={y}
                    width={columnWidth}
                    height={rowHeight}
                    style={{ fill: isAbsent ? "#80808044" : fillColor }}
                  />
                  <line x1={x + columnWidth} y1={y} x2={x + columnWidth} y2={y + rowHeight} className={styles.gridTick} />
                  
                  {(isWorkday || viewMode !== ViewMode.Day) && hours === 0 && !inPercentage && (
                    <circle cx={x + columnWidth / 2} cy={y + rowHeight / 2} r={4.5} fill="#80808033" />
                  )}
                  
                  <text
                    x={x + columnWidth / 2}
                    y={y + rowHeight / 2}
                    className={styles.workloadText}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    data-date ={dateFormater(date)}
                    style={{ fill: hours > 0 ? "#333" : "#bbb", fontWeight: hours > 0 ? 500 : 400 }}
                  >
                    {isAbsent ? 0 : isWorkday || viewMode !== ViewMode.Day ? (
                      hours > 0 || inPercentage ? (
                        inPercentage ? (
                          <>
                            <tspan x={x + columnWidth / 2} dy="-0.4em">{`${Math.round(hours)}%`} /</tspan>
                            <tspan x={x + columnWidth / 2} dy="1.2em">{"100%"}</tspan>
                          </>
                        ) : (
                          `${Number(hours.toFixed(1))} / ${maxHours}`
                        )
                      ) : ""
                    ) : ""}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  );
});

// --------------------- Virtualized left list ---------------------
// const VirtualizedList: React.FC<{
//   flatRows: FlatRow[]; // full flattened rows
//   visibleCount: number;
//   rowHeight: number;
//   taskListWidth: number;
//   expanded: string[];
//   toggleExpand: (id: string) => void;
//   handleSelectedProjectHighlight: (id: string) => void;
//   navigateToProject: (projectId: string) => void;
// }> = React.memo(({
//   flatRows,
//   visibleCount,
//   rowHeight,
//   taskListWidth,
//   expanded,
//   toggleExpand,
//   handleSelectedProjectHighlight,
//   navigateToProject,
// }) => {
//   const { t } = useTranslation();

//   const totalHeight = flatRows.length * rowHeight;
//   const visibleRows = flatRows.slice(0, visibleCount);

//   return (
//     <div
//       className={styles.listWrapper}
//       style={{ width: taskListWidth, minWidth: taskListWidth, position: "relative" }}
//       data-component="workload"
//     >
//       <div className={styles.listHeader} style={{ height: rowHeight }} data-component="workload">
//         {t("Assigned member")}
//       </div>

//       {/* spacer to create full scroll height */}
//       <div style={{ height: totalHeight, position: "relative" }}>
//         {/* Render visible rows absolutely positioned */}
//         {visibleRows.map((fr) => {
//           const index = flatRows.indexOf(fr);
//           const top = index * rowHeight;
//           const hasChildren = fr.node.children && fr.node.children.length > 0;
//           const isExpanded = expanded.includes(fr.node.id);

//           return (
//             <div
//               key={`${fr.node.id}-${index}`}
//               style={{
//                 position: "absolute",
//                 top,
//                 left: 0,
//                 right: 0,
//                 height: rowHeight,
//                 paddingLeft: `${fr.level * 16}px`,
//                 display: "flex",
//                 alignItems: "center",
//                 gap: "8px",
//                 boxSizing: "border-box",
//                 cursor: fr.node.type !== "user" ? "pointer" : "default",
//               }}
//               data-component="workload"
//             >
//               {hasChildren ? (
//                 <button
//                   className="mr-2 flex items-center"
//                   onClick={() => toggleExpand(fr.node.id)}
//                   style={{ background: "transparent", border: "none", padding: 6 }}
//                 >
//                   {isExpanded ? (
//                     <ChevronDown size={14} />
//                   ) : (
//                     <ChevronRight size={14} />
//                   )}
//                 </button>
//               ) : (
//                 <span style={{ width: 20 }} />
//               )}

//               {fr.node.type === "user" && fr.node.user && (
//                 <UserAvatar
//                   user={fr.node.user}
//                   className="mr-2 shadow-sm border-primary-300 border-2"
//                 />
//               )}

//               <div
//                 className="truncate w-full"
//                 onClick={() => {
//                   if (fr.node.type !== "user") {
//                     handleSelectedProjectHighlight(fr.node.id);
//                     // if it's a project id pattern, attempt to navigate
//                     if (fr.node.type === "project") {
//                       // attempt to extract project id if matches original pattern used earlier
//                       // fallback: navigateToProject expects the raw id if not pattern matched
//                       let projectId = fr.node.id;
//                       const parts = projectId.split("project");
//                       if (parts.length > 1) {
//                         const left = parts[1];
//                         if (left.startsWith("-")) projectId = left.slice(1);
//                         else projectId = left;
//                       }
//                       navigateToProject(projectId);
//                     }
//                   }
//                 }}
//               >
//                 {fr.node.name}
//               </div>
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// });

// --------------------- MAIN COMPONENT ---------------------
type WorkloadProps = {
  apiResourceData?: any;
  tasks: BarTaskTyped[];
  dates: Date[];
  svgWidth: number;
  columnWidth: number;
  rowHeight: number;
  headerHeight: number;
  taskListWidth: number;
  scrollX: number;
  fontFamily: string;
  fontSize: string;
  nonWorkingDays: number[];
  holidays: Date[];
  resourceMember: AssignedUsers[];
  workloadCalender: JSX.Element;
  showCalender: React.MutableRefObject<number>;
  onScroll: (event: SyntheticEvent<HTMLDivElement>) => void;
  viewMode: ViewMode;
  handleSelectedProjectHighlight: (id: string) => void;
  setAPIData?: (data: any) => void;
  setIsProject?: (data: boolean) => void;
  isPercentage?: boolean;
  workloadLoader?: boolean;
  projectName?: string;
  userAvailabilityByUserId?: Record<string, { startDate: string; endDate?: string }[]>;
};

const getMergedUserWithAvailability = (
  user: AssignedUsers["user"],
  userAvailabilityByUserId?: Record<string, { startDate: string; endDate?: string }[]>
): UserType => {
  const base = user as unknown as UserType;
  const availability = userAvailabilityByUserId?.[user.userId] ?? (base as any)?.userAvailability ?? [];
  return { ...base, userAvailability: availability };
};

const calculateWorkloadData = (
  tasks: TaskWithSubtasks[],
  nonWorkingDays: number[],
  holidays: Date[],
  inPercentage: boolean,
  assignedUsers: AssignedUsers[],
  dates: Date[],
  viewMode: ViewMode,
  userAvailabilityByUserId?: Record<string, { startDate: string; endDate?: string }[]>
): Promise<{ resources: Resource[]; dailyWorkload: DailyWorkload }> => {

  /** -----------------------------------------------------------------
   * Extract only leaf tasks and full parent path for each leaf
   * -----------------------------------------------------------------*/
  function getLeafTasks(task: TaskWithSubtasks) {
    const leaves: { leaf: TaskWithSubtasks; parents: TaskWithSubtasks[] }[] = [];

    function dfs(node: TaskWithSubtasks, parents: TaskWithSubtasks[]) {
      const path = [...parents, node];

      if (!node.subtask || node.subtask.length === 0) {
        leaves.push({ leaf: node, parents: path });
        return;
      }

      node.subtask.forEach((child) => dfs(child, path));
    }

    dfs(task, []);
    return leaves;
  }

  return new Promise((resolve) => {
    requestIdleCallback(() => {
      const resources = new Map<string, Resource>();
      const dailyWorkload: DailyWorkload = new Map();
      
      const isWorkingDay = createIsWorkingDayChecker(nonWorkingDays, holidays);
      const getViewKey = createViewKeyFinder(dates, viewMode);
      const getWorkableHoursInView = createWorkableHoursCalculator(viewMode, isWorkingDay);

      /** -----------------------------------------------------------------
       * Safe workload updater
       * -----------------------------------------------------------------*/
      const updateWorkload = (id: string, dateKey: string, hours: number, leave: number) => {
        
        if (!dailyWorkload.has(id)) dailyWorkload.set(id, new Map());
        const map = dailyWorkload.get(id)!;

        const prev = map.get(dateKey) || 0;
        let value = Math.round((prev + hours) * 100) / 100;

        if (viewMode === ViewMode.Day && value > 16 && !inPercentage) {
          value -= leave * 8;
        }

        map.set(dateKey, value);
      };

      /** -----------------------------------------------------------------
       * Pre-init all users
       * -----------------------------------------------------------------*/
      assignedUsers.forEach((assign) => {
        const userId = assign.user?.userId;
        if (!userId || resources.has(userId)) return;

        const name = assign.user?.firstName
          ? `${assign.user.firstName} ${assign.user.lastName}`
          : assign.user.email || `User ${userId}`;

        resources.set(userId, {
          id: userId,
          name,
          type: "user",
          user: getMergedUserWithAvailability(assign.user, userAvailabilityByUserId),
          children: []
        });
      });

      /** -----------------------------------------------------------------
       * 1. Collect ALL leaf task paths from ALL roots
       * -----------------------------------------------------------------*/
      const allLeafPaths: { leaf: any; parents: any[] }[] = [];

      tasks.forEach((root) => {
        const leaves = getLeafTasks(root);
        allLeafPaths.push(...leaves);
      });

      /** -----------------------------------------------------------------
       * 2. Deduplicate: keep ONLY the deepest path for each leaf
       * -----------------------------------------------------------------*/
      const uniqueLeafPaths = new Map<string, { leaf: any; parents: any[] }>();

      allLeafPaths.forEach(({ leaf, parents }) => {
        const key = leaf.id;
        const existing = uniqueLeafPaths.get(key);

        if (!existing || parents.length > existing.parents.length) {
          uniqueLeafPaths.set(key, { leaf, parents });
        }
      });

      const finalLeafPaths = Array.from(uniqueLeafPaths.values());

      /** -----------------------------------------------------------------
       * 3. Process ONLY deepest leaf paths
       * -----------------------------------------------------------------*/
      finalLeafPaths.forEach(({ leaf, parents }) => {
        if (leaf.type === "milestone") return;

        const startDate = new Date(leaf.start);
        const endDate = new Date(leaf.end);

        /** Collect working days */
        const workingDays: Date[] = [];
        const d = new Date(startDate);

        while (d <= endDate) {
          if (isWorkingDay(new Date(d))) workingDays.push(new Date(d));
          d.setDate(d.getDate() + 1);
        }

        if (!workingDays.length) return;

        leaf.assign?.forEach((assign: any) => {
          const userId = assign.user?.userId;
          if (!userId) return;

          const userNode = resources.get(userId);
          if (!userNode) return;

          /** -----------------------------------------------------------------
           * Build hierarchy: user → parent → parent → leaf
           * -----------------------------------------------------------------*/
          let pointer = userNode;

          // Parent nodes except the leaf itself
          parents.slice(0, -1).forEach((parent) => {
            const levelId = `${userId}-lvl-${parent.id}`;

            let child = pointer.children?.find((c) => c.id === levelId);
            if (!child) {
              child = {
                id: levelId,
                name: parent.name,
                type: parent.type || "task",
                user: userNode.user,
                children: []
              };
              pointer.children!.push(child);
            }

            pointer = child;
          });

          /** Create leaf node */
          const leafId = `${userId}-leaf-${leaf.id}`;

          let leafNode = pointer.children?.find((c) => c.id === leafId);
          if (!leafNode) {
            leafNode = {
              id: leafId,
              name: leaf.name,
              type: "task",
              user: userNode.user,
              children: []
            };
            pointer.children!.push(leafNode);
          }

          /** -----------------------------------------------------------------
           * Workload calculation
           * -----------------------------------------------------------------*/
          const leave = countLeaveDaysInRange(
            getMergedUserWithAvailability(assign.user, userAvailabilityByUserId)?.userAvailability ?? [],
            startDate,
            endDate
          );

          const effectiveDuration =
            viewMode === ViewMode.Day ? leaf.duration - leave || 1 : leaf.duration;

          const dailyHours = Number(assign.workingHours || 0) / effectiveDuration;
          if (!dailyHours) return;

          workingDays.forEach((day) => {
            const dateKey = getViewKey(day);
            
            if (!dateKey) return;

            let hoursToAdd = dailyHours;
           if (inPercentage) {
          // getViewKey(day) returned a "dd/mm/yyyy" period-start string
              const periodStartKey = dateKey;
              const periodStartDate = parseDateKey(periodStartKey);
              if (!periodStartDate) {
                hoursToAdd = 0;
              } else {
                const maxHours = getWorkableHoursInView(periodStartDate);
                hoursToAdd = maxHours > 0 ? (dailyHours / maxHours) * 100 : 0;
            }
          }


            if (hoursToAdd > 0) {
              // User
              updateWorkload(userId, dateKey, hoursToAdd, leave);

              // All parent levels
              parents.slice(0, -1).forEach((p) => {
                const id = `${userId}-lvl-${p.id}`;
                updateWorkload(id, dateKey, hoursToAdd, leave);
              });

              // Leaf
              updateWorkload(leafId, dateKey, hoursToAdd, leave);
            }
          });
        });
      });
      resolve({
        resources: Array.from(resources.values()),
        dailyWorkload
      });
    });
  });
};


const calculateProjectWorkloadData = (
  projects: TaskWithSubtasks[],
  nonWorkingDays: number[],
  holidays: Date[],
  inPercentage: boolean,
  assignedUsers: AssignedUsers[],
  viewMode: ViewMode,
  dates: Date[],
  userAvailabilityByUserId?: Record<string, { startDate: string; endDate?: string }[]>
): Promise<{ resources: Resource[]; dailyWorkload: DailyWorkload }> => {
  return new Promise((resolve) => {
    requestIdleCallback(() => {
      const resources = new Map<string, Resource>();
      const dailyWorkload: DailyWorkload = new Map();

      const totalHours = new Map<string, number>();
      const processedAssignments = new Set<string>();

      const isWorkingDay = createIsWorkingDayChecker(nonWorkingDays, holidays);
      const allDates = dates;
      
      const getViewKey = createViewKeyFinder(allDates, viewMode);
      const getWorkableHoursInView = createWorkableHoursCalculator(viewMode, isWorkingDay);

      const updateWorkload = (id: string, dateKey: string, hours: number, leave: number) => {
        if (!dailyWorkload.has(id)) dailyWorkload.set(id, new Map());
        const nodeWorkload = dailyWorkload.get(id)!;
        const current = nodeWorkload.get(dateKey) || 0;
        let calculated = Math.round((current + hours) * 100) / 100;

        calculated =
          viewMode === ViewMode.Day && calculated > 16 && !inPercentage
            ? calculated - leave * 8
            : calculated;

        nodeWorkload.set(dateKey, calculated);
      };

      const addToTotalHours = (id: string, hours: number) => {
        const current = totalHours.get(id) || 0;
        totalHours.set(id, Math.round((current + hours) * 100) / 100);
      };

      // Pre-populate users
      assignedUsers.forEach((assign) => {
        const user = assign.user;
        if (!user?.userId || resources.has(user.userId)) return;

        resources.set(user.userId, {
          id: user.userId,
          name:
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email || `User ${user.userId}`,
          type: "user",
          user: getMergedUserWithAvailability(user, userAvailabilityByUserId),
          children: [],
          totalHours: 0,
        });
      });

      // Process task workload
      projects?.forEach((project) => {
        project.subtask?.forEach((task) => {
          if (task.subtask && task.subtask.length > 0) {
            return; 
          }
          if (task.type && task.type === "milestone") {
            return; 
          }
          const startDate = new Date(task.start);
          const endDate = new Date(task.end);

          const workingDays: Date[] = [];
          const d = new Date(startDate);

          while (d <= endDate) {
            if (isWorkingDay(new Date(d))) workingDays.push(new Date(d));
            d.setDate(d.getDate() + 1);
          }

          if (!workingDays.length) return;

          task.assign?.forEach((assign) => {
            const userId = assign.user?.userId;
            if (!userId) return;

            const userNode = resources.get(userId);
            if (!userNode) return;

            const projectId = `${userId}-project-${project.id}`;
            const taskId = `${projectId}-task-${task.id}`;

            const assignmentKey = `${userId}-${project.id}-${task.id}-${assign.workingHours}`;

            let projectNode = userNode.children?.find((c) => c.id === projectId);
            if (!projectNode) {
              projectNode = {
                id: projectId,
                name: project.name,
                type: "project",
                user: userNode.user,
                children: [],
                totalHours: 0,
              };
              userNode.children!.push(projectNode);
            }

            let taskNode = projectNode.children?.find((c) => c.id === taskId);
            if (!taskNode) {
              taskNode = {
                id: taskId,
                name: task.name,
                type: "task",
                user: userNode.user,
                totalHours: 0,
              };
              projectNode.children!.push(taskNode);
            }

            const assignedUser = assignedUsers.find((u) => u.user.userId === userId)?.user;
            const leave = countLeaveDaysInRange(
              assignedUser ? getMergedUserWithAvailability(assignedUser, userAvailabilityByUserId)?.userAvailability ?? [] : [],
              startDate,
              endDate
            );

            const effectiveDuration =
              viewMode == ViewMode.Day ? task.duration - leave || 0 : task.duration;

            const dailyHours = Number(assign.workingHours || 0) / effectiveDuration;

            if (dailyHours === 0) return;

            if (!inPercentage && !processedAssignments.has(assignmentKey)) {
              const hours = Number(assign.workingHours || 0);
              processedAssignments.add(assignmentKey);
              addToTotalHours(taskId, hours);
              addToTotalHours(projectId, hours);
              addToTotalHours(userId, hours);
            }

            workingDays.forEach((day) => {
              const viewKey = getViewKey(day);
              
              if (!viewKey) return;

              let hoursToAdd = dailyHours;

             if (inPercentage) {
              const periodStart = parseDateKey(viewKey);   // FIXED
              const maxHours = getWorkableHoursInView(periodStart as any); // CORRECT
              const safeMax = Number(maxHours);
              const safeDaily = Number(dailyHours);

              hoursToAdd =
                safeMax > 0 && Number.isFinite(safeMax) && Number.isFinite(safeDaily)
                  ? (safeDaily / safeMax) * 100
                  : 0;
            }

              if (hoursToAdd > 0) {
                updateWorkload(userId, viewKey, hoursToAdd, leave);
                updateWorkload(projectId, viewKey, hoursToAdd, leave);
                updateWorkload(taskId, viewKey, hoursToAdd, leave);
              }
            });
          });
        });
      });

      // Set totals
      resources.forEach((userNode) => {
        userNode.totalHours = totalHours.get(userNode.id) || 0;

        userNode.children?.forEach((projectNode) => {
          projectNode.totalHours = totalHours.get(projectNode.id) || 0;

          projectNode.children?.forEach((taskNode) => {
            taskNode.totalHours = totalHours.get(taskNode.id) || 0;
          });
        });
      });
      resolve({
        resources: Array.from(resources.values()),
        dailyWorkload,
      });
    });
  });
};

// Extract dates automatically from tasks


export const WorkloadComponent: React.FC<WorkloadProps> = ({
  tasks,
  dates,
  svgWidth,
  columnWidth,
  rowHeight,
  headerHeight,
  taskListWidth,
  scrollX,
  fontFamily,
  nonWorkingDays,
  holidays,
  resourceMember,
  onScroll,
  workloadCalender,
  showCalender,
  viewMode,
  handleSelectedProjectHighlight,
  setIsProject,
  setAPIData,
  apiResourceData,
  isPercentage,
  workloadLoader,
  projectName,
  userAvailabilityByUserId,
}) => {
  const gridWrapperRef = useRef<HTMLDivElement | null>(null); // right container (grid)
  const listWrapperRef = useRef<HTMLDivElement | null>(null); // left container (list)
  const { t } = useTranslation();

  const [inPercentage, setInPercentage] = useState(isPercentage?? true);
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });
  const [expanded, setExpanded] = useState<string[]>([]);
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
  const [loader, setLoader] = useState(true);
  const [workloadData, setWorkloadData] = useState<{
    resources: Resource[];
    dailyWorkload: DailyWorkload;
  }>({ resources: [], dailyWorkload: new Map() });

  // visible rows (for both sides) — initial + load more
  const [visibleRowCount, setVisibleRowCount] = useState<number>(INITIAL_VISIBLE_ROWS);
  const [xlsxDates, setXlsxDates] = useState<Date[]>([]);
  // convert API dailyWorkload (object) to Map
  const convertDailyWorkloadToMap = useCallback((data: any): DailyWorkload => {
    const outerMap = new Map<string, Map<string, number>>();
    Object.entries(data || {}).forEach(([userId, dateObj]: any) => {
      const innerMap = new Map<string, number>();
      Object.entries(dateObj || {}).forEach(([dateKey, value]: any) => {
        innerMap.set(dateKey, Number(value));
      });
      outerMap.set(userId, innerMap);
    });
    return outerMap;
  }, []);

  // setIsProject checkbox handler
  const handleCheckbox = useCallback(() => {
    setIsAllChecked((prev) => {
      const newState = !prev;
      setIsProject?.(newState);
      return newState;
    });
  }, [setIsProject]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]);
    // when expanding/collapsing, reset visibleRowCount to ensure we show top rows
    setVisibleRowCount(INITIAL_VISIBLE_ROWS);
    // also scroll both containers back to top for clarity
    if (gridWrapperRef.current) gridWrapperRef.current.scrollTop = 0;
    if (listWrapperRef.current) listWrapperRef.current.scrollTop = 0;
  }, []);

  const selectOptions = useMemo(
    () => [
      { label: t("Loads in hours"), value: "number" },
      { label: t("Loads in percentage"), value: "%" },
    ],
    [t]
  );

  useEffect(() => {
    const apiData = {
      isWorkloadPlanning: window.location.pathname.includes("capacity-planning"),
      viewMode,
      inPercentage,
    };
    setAPIData?.(apiData);
  }, [viewMode, inPercentage, setAPIData]);

  // Sync horizontal scroll (scrollX) — keep the grid scrolled to same left position
  useEffect(() => {
    if (gridWrapperRef.current) {
      gridWrapperRef.current.scrollLeft = scrollX;
    }
  }, [scrollX]);

  // Load / calculate workloadData (use your existing functions)
// Load / calculate workloadData (CORRECT VERSION)
const [XlsxData, setXlsxData] =useState<{
    resources: Resource[];
    dailyWorkload: DailyWorkload;
  }>({ resources: [], dailyWorkload: new Map() });
  
useEffect(() => {
  setLoader(true);
  let isCancelled = false;

  const isWorkloadPlanning =
    isAllChecked || window.location.pathname.includes("capacity-planning");

  // Case 1: API resource data provided → just map it
  
  if (apiResourceData) {
    
    setWorkloadData({
      resources: apiResourceData.resources.sort((a: any, b: any) => a.name.localeCompare(b.name)),
      dailyWorkload: convertDailyWorkloadToMap(apiResourceData.dailyWorkload),
    });
    if (viewMode === ViewMode.Day) {
      setXlsxData({
        resources: apiResourceData.resources.sort((a: any, b: any) => a.name.localeCompare(b.name)),
        dailyWorkload: convertDailyWorkloadToMap(apiResourceData.dailyWorkload),
      });
    }
    setLoader(false);
    return () => (isCancelled = true);
  }

  // Case 2: Calculate workload normally
  const calculationPromise = isWorkloadPlanning
    ? calculateProjectWorkloadData(
        tasks as any,
        nonWorkingDays,
        holidays,
        inPercentage,
        resourceMember,
        viewMode,
        dates,
        userAvailabilityByUserId
      )
    : calculateWorkloadData(
        tasks as any,
        nonWorkingDays,
        holidays,
        inPercentage,
        resourceMember,
        dates,
        viewMode,
        userAvailabilityByUserId
      );

  calculationPromise
    .then((res) => {
      if (!isCancelled) {
        setWorkloadData({resources: res.resources.sort((a: any, b: any) => a.name.localeCompare(b.name)), dailyWorkload: res.dailyWorkload});
        if (viewMode === ViewMode.Day) {
          setXlsxData({resources: res.resources.sort((a: any, b: any) => a.name.localeCompare(b.name)), dailyWorkload: res.dailyWorkload});
        }
        // reset visible rows on recalculation
        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
        setTimeout(() => setLoader(false), 300);
      }
    })
    .catch(() => {
      if (!isCancelled) setTimeout(() => setLoader(false), 300);
    });

  return () => {
    isCancelled = true;
  };
}, [
  tasks,
  resourceMember,
  inPercentage,
  nonWorkingDays,
  holidays,
  viewMode,
  isAllChecked,
  dates,
  apiResourceData,
  userAvailabilityByUserId,
  convertDailyWorkloadToMap,
]);

  // compute the flattened array for rendering
  const flatRows = useMemo(() => {
    return flattenResources(workloadData.resources || [], expanded);
  }, [workloadData.resources, expanded]);

  // maintain a ref to flatRows for scroll handlers
  const flatRowsRef = useRef<FlatRow[]>([]);
  useEffect(() => {
    flatRowsRef.current = flatRows;
    // ensure visibleRowCount doesn't exceed total rows
    setVisibleRowCount((prev) => Math.min(Math.max(INITIAL_VISIBLE_ROWS, prev), flatRows.length || INITIAL_VISIBLE_ROWS));
  }, [flatRows]);

  // visible slice for both sides (we render first visibleRowCount rows)
  const visibleFlatRows = useMemo(() => flatRows, [flatRows, visibleRowCount]);

  // RIGHT side scroll handler: triggers load-more and syncs left scroll
  const handleRightScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    onScroll?.(e); // existing callback
    
    const el = e.currentTarget as HTMLDivElement;
    if (!el) return;
    

    // scroll-sync: set left scrollTop equal to right scrollTop (they share same totalHeight)
    if (listWrapperRef.current) {
      // we map vertical scroll directly — both containers have identical total scroll height
      // If left wrapper has a header height difference, adjust accordingly — here headers are similar heights
      listWrapperRef.current.scrollTop = el.scrollTop;
    }

    // load more when near bottom
    const scrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_THRESHOLD;
    if (scrolledToBottom) {
      setVisibleRowCount((prev) => {
        const next = Math.min(flatRowsRef.current.length || 0, prev + LOAD_MORE_COUNT);
        return next > prev ? next : prev;
      });
    }
  }, [onScroll]);

  // left list scrolling should mirror right grid (listen to left scroll and set right top?) — we follow option A: right triggers load-more.
  // To make vertical pointer interactions smooth, sync both ways minimally:
  useEffect(() => {
    const leftEl = listWrapperRef.current;
    const rightEl = gridWrapperRef.current;
    if (!leftEl || !rightEl) return;

    // when left is scrolled by user, update right scroll position to match
    const onLeftScroll = () => {
      // only sync if user scrolls left; skip if programmatic (we can't detect easily) — keep lightweight
      rightEl.scrollTop = leftEl.scrollTop;
    };
    leftEl.addEventListener("scroll", onLeftScroll);
    return () => leftEl.removeEventListener("scroll", onLeftScroll);
  }, []);

  useEffect(() => {
    if (viewMode === ViewMode.Day) {
      setXlsxDates(dates);
    }
  }, [dates, viewMode]);

  // compute svg total height to allow full scroll (we render only first visible rows' <g> elements positioned by index)
  const svgTotalHeight = Math.max((flatRows.length || 0) * rowHeight, 1);
  return (
    <div
      className={`${styles.workloadContainer} relative ${loader ? "backdrop-blur-md" : ""} ${showCalender.current === 0 ? "mt-[49px]" : ""}`}
      data-component="workload"
      onScroll={(e) => e.preventDefault()}
    >
      {workloadData.resources.length === 0 && !loader && (
        <div className="flex justify-center items-center text-sm text-gray-400 absolute top-3 left-0 h-full w-full">
          {t("No team member has been assigned to any task yet. Please assign members to tasks to view the workload.")}
        </div>
      )}

      {loader || workloadLoader && (
        <Loader className="absolute top-3 left-0 h-full w-full animate-in duration-200 bg-white/65 backdrop-blur-md" />
      )}

      {/* LEFT: virtualized list container */}
      <div
        ref={listWrapperRef as React.RefObject<HTMLDivElement>}
        className={styles.listWrapper}
        style={{ width: taskListWidth, minWidth: taskListWidth, overflowY: "auto", height: "100%" }}
        data-component="workload"
      >
        <div className={styles.listHeader} style={{ height: showCalender.current === 0 ? headerHeight:headerHeight-30 }} data-component="workload">
          {window.location.pathname.includes("capacity-planning") ? t("Workload_Members") : t("Assigned member")}
        </div>

        {/* Virtualized list rendering */}
        <div style={{ height: svgTotalHeight, position: "relative" }}>
          {visibleFlatRows.map((fr) => {
            const originalIndex = flatRows.indexOf(fr);
            const top = originalIndex * rowHeight;
            const hasChildren = fr.node.children && fr.node.children.length > 0;
            const isExpanded = expanded.includes(fr.node.id);
            return (
              <div
                key={`${fr.node.id}-${originalIndex}`}
                style={{
                  position: "absolute",
                  top,
                  left: 0,
                  right: 0,
                  height: rowHeight,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  paddingLeft: fr.level * 16,
                  boxSizing: "border-box",
                  cursor: fr.node.type !== "user" ? "pointer" : "default",
                }}
                data-component="workload"
              >
                {hasChildren ? (
                  <button onClick={() => toggleExpand(fr.node.id)} style={{ border: "none", background: "transparent", padding: 6 }}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                ) : <span style={{ width: 20 }} />}

                {fr.node.type === "user" && fr.node.user && (
                  <UserAvatar user={fr.node.user} className="mr-2 shadow-sm border-primary-300 border-2" />
                )}

                <div
                  className="truncate w-full"
                  onClick={() => {
                    if (fr.node.type !== "user") {
                      if (fr.node.type === "project") {
                        let projectId = fr.node.id;
                        const parts = projectId.split("project");
                        if (parts.length > 1) {
                          const left = parts[1];
                          if (left.startsWith("-")) projectId = left.slice(1);
                          else projectId = left;
                        }
                        handleSelectedProjectHighlight(projectId);
                      }
                      if (fr.node.type === "task") {
                        let taskId = fr.node.id;
                        const parts = taskId.split("task");
                        if (parts.length > 1) {
                          const left = parts[1];
                          if (left.startsWith("-")) taskId = left.slice(1);
                          else taskId = left;
                        }
                        handleSelectedProjectHighlight(taskId);
                      }
                    }
                  }}
                >
                  {fr.node.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: workload grid container */}
      <div
        ref={gridWrapperRef as React.RefObject<HTMLDivElement>}
        className={styles.gridWrapper}
        data-component="workload"
        onScroll={handleRightScroll}
        style={{ overflow: "auto" }}
      >
        {showCalender.current === 0 && workloadCalender}

        <div className={`absolute right-0 flex gap-2 ${showCalender.current === 0 ? "-top-11" : "top-1"}`} data-component="workload">
          {!window.location.pathname.includes("capacity-planning") && (
            <div className="justify-center flex items-center gap-2">
              <input type="checkbox" className="accent-primary-300" checked={isAllChecked} onChange={handleCheckbox} />
              <label className="text-gray-450">{t("ALL_PROJECTS")}</label>
            </div>
          )}
          <Button variant={"outline"} className="px-2 py-1 h-fit mt-1" onClick={async () => {
            await downloadWorkloadXlsx({
              resources: XlsxData.resources,
              dailyWorkload: XlsxData.dailyWorkload,
              dates: xlsxDates,
              inPercentage,
              nonWorkingDays,
              holidays,
              filename: projectName ? `${projectName}-workload-report.xlsx` : "workload-report.xlsx",
            });
          }}>
            {t("export_as_excel")}
          </Button>
          <Select
            styles={reactSelectStyle}
            className="w-56 scale-90"
            defaultValue={selectOptions[1]}
            options={selectOptions}
            onChange={(e: any) => setInPercentage(e?.value === "%")}
            data-component="workload"
          />
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          data-component="workload"
          className={showCalender.current !== 0 ? "mt-[49px]" : ""}
          width={svgWidth}
          height={`${svgTotalHeight}px`} // full total height for scroll space
          fontFamily={fontFamily}
        >
          {/* Pass visible slice to WorkloadGrid; positions inside use original indices */}
          <WorkloadGrid
            flatRows={visibleFlatRows.map((r) => {
              // attach originalIndex so WorkloadGrid can position correctly
              const originalIndex = flatRows.indexOf(r);
              return Object.assign({}, r, { originalIndex });
            })}
            dates={dates}
            workloadData={workloadData.dailyWorkload}
            rowHeight={rowHeight}
            columnWidth={columnWidth}
            nonWorkingDays={nonWorkingDays}
            holidays={holidays}
            inPercentage={inPercentage}
            setTooltip={setTooltip}
            viewMode={viewMode}
          />
        </svg>
      </div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className="fixed z-50 px-3 py-2 text-sm bg-white border border-gray-200/50 rounded shadow-lg pointer-events-none"
          style={{
            top: tooltip.y + 12,
            left: tooltip.x + 12,
            whiteSpace: "pre-wrap",
            maxWidth: "240px",
          }}
        >
          {tooltip.content}
        </div>
      )}

      
    </div>
  );
};

export const Workload = React.memo(WorkloadComponent);
